import React, { useEffect, useState, useMemo } from 'react';
import { Save, Calculator } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { adminGetPricing, adminUpdatePricing } from '../api/api';

const LIBELLES_FREQUENCE = {
  ponctuel: 'Une seule fois',
  mensuel: 'Une fois par mois',
  bimensuel: 'Deux fois par mois',
  hebdomadaire: 'Une fois par semaine',
  quotidien: 'Tous les jours',
};

const LIBELLES_TYPE = {
  bureaux: 'Bureaux',
  commerce: 'Commerce',
  domicile: 'Domicile',
  immeuble: "Parties communes d'immeuble",
  chantier: 'Fin de chantier',
  vitres: 'Vitrerie',
};

export const AdminPricing = () => {
  const [reglages, setReglages] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);

  useEffect(() => {
    adminGetPricing()
      .then(setReglages)
      .catch(() => toast.error('Chargement des tarifs impossible.'));
  }, []);

  // Aperçu calculé en direct depuis les valeurs du formulaire (mêmes formules
  // que le serveur) : chaque réglage se répercute immédiatement, sans enregistrer.
  const apercu = useMemo(() => {
    if (!reglages?.enabled) return null;
    const surface = 150;
    const fFreq = parseFloat(reglages.frequency_factors?.hebdomadaire);
    const fType = parseFloat(reglages.type_factors?.bureaux);
    const taux = parseFloat(reglages.base_rate_per_m2);
    const mini = parseFloat(reglages.minimum_price);
    const marge = parseFloat(reglages.margin_percent) / 100;
    if ([fFreq, fType, taux, mini, marge].some((n) => Number.isNaN(n))) return null;
    let base = surface * taux * fFreq * fType;
    base = Math.max(base, mini);
    return { low: base * (1 - marge / 2), high: base * (1 + marge / 2) };
  }, [reglages]);

  const enregistrer = async () => {
    setEnregistrement(true);
    try {
      const maj = await adminUpdatePricing({
        enabled: reglages.enabled,
        base_rate_per_m2: parseFloat(reglages.base_rate_per_m2),
        minimum_price: parseFloat(reglages.minimum_price),
        margin_percent: parseFloat(reglages.margin_percent),
        frequency_factors: reglages.frequency_factors,
        type_factors: reglages.type_factors,
        disclaimer: reglages.disclaimer,
      });
      setReglages(maj);
      toast.success('Tarifs enregistrés.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Enregistrement impossible.');
    } finally {
      setEnregistrement(false);
    }
  };

  const majCoefficient = (groupe, cle, valeur) =>
    setReglages((r) => ({
      ...r,
      [groupe]: { ...r[groupe], [cle]: parseFloat(valeur) || 0 },
    }));

  if (!reglages) return <div className="p-8 text-gray-500">Chargement…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Estimateur de prix</h1>
        <p className="text-gray-600 mt-1 max-w-2xl">
          L'estimateur affiché sur le site calcule une fourchette à partir de ces réglages.
          Les taux restent sur le serveur : ils ne sont jamais visibles par le visiteur.
        </p>
      </div>

      <label className="flex items-center gap-3 cursor-pointer bg-white border border-gray-200 rounded-lg p-4">
        <input
          type="checkbox"
          checked={reglages.enabled}
          onChange={(e) => setReglages((r) => ({ ...r, enabled: e.target.checked }))}
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
        />
        <span className="text-sm font-medium text-gray-900">
          Afficher l'estimateur sur le site
        </span>
      </label>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Tarif de base</CardTitle>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="taux">Prix au m²</Label>
                <Input
                  id="taux"
                  type="number"
                  step="0.01"
                  min="0"
                  value={reglages.base_rate_per_m2}
                  onChange={(e) =>
                    setReglages((r) => ({ ...r, base_rate_per_m2: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mini">Prix plancher</Label>
                <Input
                  id="mini"
                  type="number"
                  step="1"
                  min="0"
                  value={reglages.minimum_price}
                  onChange={(e) => setReglages((r) => ({ ...r, minimum_price: e.target.value }))}
                />
                <p className="text-xs text-gray-500">Aucune estimation en dessous.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="marge">Largeur de fourchette (%)</Label>
                <Input
                  id="marge"
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={reglages.margin_percent}
                  onChange={(e) => setReglages((r) => ({ ...r, margin_percent: e.target.value }))}
                />
                <p className="text-xs text-gray-500">Écart entre le bas et le haut.</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Coefficients par fréquence</CardTitle>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              {Object.entries(reglages.frequency_factors).map(([cle, valeur]) => (
                <div key={cle} className="space-y-2">
                  <Label htmlFor={`f-${cle}`}>{LIBELLES_FREQUENCE[cle] || cle}</Label>
                  <Input
                    id={`f-${cle}`}
                    type="number"
                    step="0.05"
                    min="0"
                    value={valeur}
                    onChange={(e) => majCoefficient('frequency_factors', cle, e.target.value)}
                  />
                </div>
              ))}
              <p className="sm:col-span-2 text-xs text-gray-500">
                Un coefficient inférieur à 1 réduit le prix par passage : c'est la remise
                accordée pour un contrat récurrent.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Coefficients par type de local</CardTitle>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              {Object.entries(reglages.type_factors).map(([cle, valeur]) => (
                <div key={cle} className="space-y-2">
                  <Label htmlFor={`t-${cle}`}>{LIBELLES_TYPE[cle] || cle}</Label>
                  <Input
                    id={`t-${cle}`}
                    type="number"
                    step="0.05"
                    min="0"
                    value={valeur}
                    onChange={(e) => majCoefficient('type_factors', cle, e.target.value)}
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Mention affichée</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={reglages.disclaimer}
                onChange={(e) => setReglages((r) => ({ ...r, disclaimer: e.target.value }))}
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-2">
                Cette mention protège juridiquement : elle rappelle que l'estimation
                n'est pas une offre ferme.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="lg:sticky lg:top-6 h-fit">
          <Card className="border-blue-100 bg-blue-50/40">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Calculator className="w-4 h-4 text-blue-600" aria-hidden="true" />
                Aperçu
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-3">
                150 m² de bureaux, une fois par semaine :
              </p>
              {apercu ? (
                <p className="text-3xl font-bold text-gray-900">
                  {Math.round(apercu.low)} – {Math.round(apercu.high)} €
                </p>
              ) : (
                <p className="text-gray-400">Estimateur désactivé</p>
              )}
              <p className="text-xs text-gray-500 mt-3">
                L'aperçu se met à jour en direct. Pensez à enregistrer pour appliquer sur le site.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={enregistrer} disabled={enregistrement} className="gap-2">
          <Save className="w-4 h-4" aria-hidden="true" />
          {enregistrement ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  );
};

export default AdminPricing;
